import moment from "moment";
import * as React from "react";
import { useCallback } from "react";
import InlineSVG from "react-inlinesvg";
import Modal from "react-modal";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { extractTextFromTipTap } from "../../../../src/shared/schemas/CharacterSchema";
import IconButton from "../IconButton";
import ScrollablePagination from "../ScrollablePagination/ScrollablePagination";
import { useHistoryPagination } from "./hooks/useHistoryPagination";
import { HistoryItem, HistoryModalProps } from "./types";

const customModalStyles = {
  content: {
    borderRadius: "20px",
    border: "none",
    boxShadow: "var(--default-drop-shadow)",
    overflow: "hidden",
    padding: "0px",
    width: "calc(100vw - 40px)",
    height: "calc(100vh - 40px)",
  },
  overlay: {
    backgroundColor: "rgba(0,0,0,0.2)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100vw",
    height: "100vh",
    zIndex: 100003,
  },
};

export default function HistoryModal({
  isOpen,
  onClose,
  selectedItem,
  historyItems,
  onSelectItem,
  onRestoreItem,
  titleIcon = "/assets/clock.svg",
  title,
  selectedItemId,
  sourceTable,
  loading,
  restoring,
  fetchHistory,
  usePagination = false,
  onToggleFavorite,
  isCreatingSnapshot = false,
  isSavingFavorite = false,
  preloadedData,
}: HistoryModalProps) {
  // Transform pagination items to match expected HistoryItem structure
  const transformPaginationItem = (item: any) => ({
    id: item.id,
    createdAt: item.createdAt,
    data: {
      // Preserve original data structure and add displayName/summary if available
      ...(item.data || {}),
      displayName: item.summary || item.data?.displayName || item.data?.name,
      isFavorite: item.isFavorite || item.data?.isFavorite,
    },
    // Preserve any other top-level properties
    ...item,
    isFavorite: item.isFavorite || item.data?.isFavorite,
  });

  const onNewItemSelectedCallback = useCallback(
    (item) => {
      // Transform the item before calling onSelectItem
      const transformedItem = transformPaginationItem(item);
      onSelectItem(transformedItem);
    },
    [onSelectItem]
  );

  const paginationHook = useHistoryPagination({
    fetchHistory: fetchHistory || (() => Promise.resolve([])),
    limit: 50,
    onNewItemSelected: onNewItemSelectedCallback,
    preloadedData: preloadedData || undefined,
  });

  const items = usePagination && fetchHistory ? paginationHook.items.map(transformPaginationItem) : historyItems || [];

  const isLoading = usePagination && fetchHistory ? paginationHook.isLoading : loading;
  const isLoadingMore = usePagination && fetchHistory ? paginationHook.isLoadingMore : false;
  const hasMore = usePagination && fetchHistory ? paginationHook.hasMore : false;
  const hasError = usePagination && fetchHistory ? paginationHook.hasError : false;
  const selectedItemFromPagination = usePagination && fetchHistory && paginationHook.selectedItem ? transformPaginationItem(paginationHook.selectedItem) : null;

  const initialLoadComplete = usePagination && fetchHistory ? paginationHook.initialLoadComplete : true;

  // Load initial data when modal opens
  React.useEffect(() => {
    if (isOpen && usePagination && fetchHistory && !paginationHook.items.length && !hasError) {
      paginationHook.loadInitialData();
    }
  }, [isOpen, usePagination, fetchHistory, hasError]);

  // Refresh data in background when modal opens to catch new snapshots
  React.useEffect(() => {
    if (isOpen && usePagination && fetchHistory) {
      // Always check for new items when modal opens, with a delay to allow initial load
      const timeoutId = setTimeout(() => {
        paginationHook.checkForNewItems();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, usePagination, fetchHistory]);

  const renderHistoryItem = (item: HistoryItem) => {
    const niceTimestamp = moment(item.createdAt).format("LLL");
    let displayName = "";
    if (item?.data?.displayName || item?.data?.name) {
      displayName = item?.data?.displayName || extractTextFromTipTap(item?.data?.name);
    }
    if (sourceTable === "outlines") {
      displayName = "";
    }

    const isSelected = usePagination && fetchHistory ? selectedItemFromPagination?.id === item.id : selectedItemId === item.id;
    const isFavorite = item?.isFavorite;

    return (
      <SudoButton
        onClick={() => {
          if (usePagination && fetchHistory) {
            paginationHook.selectItem(item);
          } else {
            onSelectItem(item);
          }
        }}
        key={item.id}
        variant={isSelected ? "primary" : "tertiaryGrey"}
        className="w-full h-auto relative"
      >
        <div className="flex flex-col items-start text-left justify-start text-sm w-full">
          <p className="history-item-date">{niceTimestamp}</p>
          {displayName.length > 0 ? <p className="history-item-title opacity-80">{displayName}</p> : null}
        </div>
        {isFavorite && (
          <InlineSVG
            src="/assets/star.fill.svg"
            className={`w-4 h-4 ${isSelected ? "fill-white" : "fill-[var(--grey-0)]"} absolute top-2 right-2`}
          />
        )}
      </SudoButton>
    );
  };
  const isMainItemFavorite = selectedItemId && items?.some((item) => item.id === selectedItemId && item?.isFavorite);

  const loadingMessage = React.useMemo(() => {
    if (isCreatingSnapshot) {
      return "Taking Snapshot...";
    }
    if (isSavingFavorite) {
      return "Saving Favorite...";
    }
    if (isLoading || isLoadingMore) {
      return "Loading...";
    }
    return null;
  }, [isCreatingSnapshot, isSavingFavorite, isLoading, isLoadingMore]);

  return (
    <Modal
      isOpen={isOpen}
      contentLabel={title}
      className="history-modal"
      style={customModalStyles}
      onRequestClose={onClose}
    >
      <div className="content relative bg-[var(--menu-color-fullscreen)] flex rounded-2xl gap-4 p-4 pr-14 overflow-hidden h-full">
        <IconButton
          src="/assets/close.svg"
          className="close absolute top-4 right-4 z-10"
          onClick={onClose}
          aria-label="Close History"
        />
        <div className="history-items flex flex-col gap-4 min-w-[250px]">
          <div className="history-items-header">
            <h2 className="fill-inherit text-lg font-bold flex items-center justify-start gap-2 text-[var(--grey-0)]">
              <InlineSVG
                src={titleIcon}
                className="w-6 h-6 fill-[var(--grey-0)]"
              />
              {title}
            </h2>
          </div>
          <div className="history-items-list h-[calc(100%-8.75rem)] rounded-lg overflow-y-scroll flex flex-col gap-2 pr-3">
            {hasError ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-[var(--grey-1)] mb-2">Failed to load history</p>
                <p className="text-sm text-[var(--grey-2)]">Please try again later</p>
              </div>
            ) : usePagination && fetchHistory ? (
              <ScrollablePagination
                onLoadMore={paginationHook.loadMore}
                hasMore={hasMore}
                isLoading={isLoading || isLoadingMore}
                initialLoadComplete={initialLoadComplete}
                className="flex flex-col gap-2"
              >
                {items.map(renderHistoryItem)}
              </ScrollablePagination>
            ) : (
              items.map(renderHistoryItem)
            )}
          </div>
        </div>
        <div className="selected-item overflow-hidden h-[calc(100%-45px)] relative flex flex-col gap-4 justify-between items-start w-full p-2">
          <div className={`scroll-container overflow-y-scroll border-[1px solid var(--divider)] w-full rounded-lg ${!React.isValidElement(selectedItem) ? "border-none" : ""}`}>
            {
              <>
                {selectedItem}
                {onToggleFavorite && selectedItemId && (
                  <IconButton
                    src={isMainItemFavorite ? "/assets/star.fill.svg" : "/assets/star.svg"}
                    triggerClassName={`absolute w-8 h-8 ${isMainItemFavorite ? "fill-white" : "fill-[var(--grey-0)]"} top-2 right-2 z-10`}
                    onClick={async () => {
                      if (onToggleFavorite) {
                        try {
                          const newIsFavorite = await onToggleFavorite(selectedItemId);

                          // Optimistically update the local state immediately
                          if (usePagination && paginationHook.updateSelectedItemFavorite && typeof newIsFavorite === "boolean") {
                            paginationHook.updateSelectedItemFavorite(selectedItemId, newIsFavorite);
                          }

                          // Still refresh data in background to ensure consistency
                          if (usePagination && paginationHook.refreshData) {
                            setTimeout(async () => {
                              await paginationHook.refreshData();
                            }, 1000); // 1 second delay for Firebase propagation
                          }
                        } catch (error) {
                          // If the toggle failed, just refresh the data to get the current state
                          if (usePagination && paginationHook.refreshData) {
                            await paginationHook.refreshData();
                          }
                        }
                      }
                    }}
                    aria-label={isMainItemFavorite ? "Remove from favorites" : "Add to favorites"}
                    hoverTipText={isMainItemFavorite ? "Remove from favorites" : "Add to favorites"}
                    disabled={isSavingFavorite}
                  />
                )}
              </>
            }
          </div>
          <div className="flex flex-row items-center justify-between w-full">
            <SudoButton
              variant="primary"
              onClick={onRestoreItem}
              disabled={loading || restoring}
            >
              Restore
            </SudoButton>
            {isLoading || isLoadingMore || isCreatingSnapshot || isSavingFavorite ? (
              <div className="toast bg-[var(--primary-cta-active)] text-white rounded-lg p-2 px-4 text-sm">{loadingMessage}</div>
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
}
